import React, { useState } from 'react';
import { Calendar, Clock, MapPin, Users, Trash2, Plus } from 'lucide-react';
import { useSchedule } from '../contexts/ScheduleContext';
import { Link } from 'react-router-dom';

// Normalize time strings to have a space before AM/PM and handle ranges
const normalizeTime = (timeStr) => {
  if (!timeStr) return '';

  // Split ranges like "09:00AM-10:30AM"
  return timeStr.split('-').map(part => {
    let [time, period] = part.match(/(\d{1,2}:\d{2})\s*([AP]M)/i)?.slice(1) || [];
    if (!time || !period) return part.trim(); // fallback if regex fails
    // Remove leading zero from hour
    let [h, m] = time.split(':');
    h = parseInt(h, 10); // convert "09" → 9
    return `${h}:${m} ${period.toUpperCase()}`;
  }).join(' - ');
};

// Convert start time to minutes for sorting
const toMinutes = (t) => {
  if (!t) return 0;
  const startTime = t.split('-')[0].trim();

  // Try AM/PM first
  const ampmMatch = startTime.match(/(\d{1,2}:\d{2})\s*([AP]M)/i);
  if (ampmMatch) {
    let [h, m] = ampmMatch[1].split(':').map(Number);
    const period = ampmMatch[2].toUpperCase();
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h * 60 + (m || 0);
  }

  // Fall back to 24-hour time
  const twentyFourMatch = startTime.match(/(\d{1,2}):(\d{2})/);
  if (twentyFourMatch) {
    const h = Number(twentyFourMatch[1]);
    const m = Number(twentyFourMatch[2]);
    return h * 60 + (m || 0);
  }

  return 0;
};

const Schedule = () => {
  const [selectedDay, setSelectedDay] = useState('day1');
  const [feedback, setFeedback] = useState({ show: false, message: '', type: '' });
  const [undoState, setUndoState] = useState({ item: null, timerId: null });
  const { userSchedule, removeFromSchedule, addToSchedule } = useSchedule();

  const normalizeCategory = (value) => {
    if (value === null || value === undefined) return '';
    return value.toString().trim().toLowerCase().replace(/\s+/g, '_');
  };

  const getEventCategoryKey = (item) => {
    const normalized = normalizeCategory(item.category);
    const tags = Array.isArray(item.tags) ? item.tags.map(t => normalizeCategory(t)) : [];

    if (normalized === 'keynote' || tags.includes('keynote')) return 'keynote';
    if (normalized === 'panel' || tags.includes('panel')) return 'panel';
    if (tags.includes('solo_talk')) return 'solo_talk';
    if (tags.includes('fireside_chat')) return 'fireside_chat';
    if (normalized === 'engagement_event' || normalized === 'engagement' || tags.includes('engagement')) {
      return 'engagement_event';
    }

    if (normalized === 'speaker_event') {
      if (tags.includes('solo_talk')) return 'solo_talk';
      if (tags.includes('fireside_chat')) return 'fireside_chat';
      if (tags.includes('panel')) return 'panel';
      return 'solo_talk';
    }

    return normalized;
  };

  const getCategoryLabel = (categoryKey) => {
    const labels = {
      keynote: 'Keynote',
      solo_talk: 'Solo Talk',
      fireside_chat: 'Fireside Chat',
      panel: 'Panel',
      engagement_event: 'Engagement Event'
    };
    return labels[categoryKey] || 'Other';
  };

  const days = [
    { value: 'day1', label: 'Day 1 - Feb 21', date: 'Saturday, Feb 21' },
    { value: 'day2', label: 'Day 2 - Feb 22', date: 'Sunday, Feb 22' }
  ];

  const getCategoryColor = (category) => {
    const colors = {
      keynote: 'bg-purple-100 text-purple-800 border-purple-200',
      solo_talk: 'bg-blue-100 text-blue-800 border-blue-200',
      fireside_chat: 'bg-orange-100 text-orange-800 border-orange-200',
      panel: 'bg-green-100 text-green-800 border-green-200',
      engagement_event: 'bg-pink-100 text-pink-800 border-pink-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const handleRemoveFromSchedule = async (scheduleId) => {
    const removedItem = userSchedule.find(item => item.id === scheduleId) || null;
    
    const result = await removeFromSchedule(scheduleId);
    setFeedback({
      show: true,
      message: result.message,
      type: result.success ? 'success' : 'error'
    });

    if (result.success) {
      if (undoState.timerId) {
        clearTimeout(undoState.timerId);
      }
      const timerId = setTimeout(() => {
        setUndoState({ item: null, timerId: null });
        setFeedback({ show: false, message: '', type: '' });
      }, 15000);
      setUndoState({ item: removedItem, timerId });
    } else {
      // Hide feedback after 3 seconds on error
      setTimeout(() => {
        setFeedback({ show: false, message: '', type: '' });
      }, 3000);
    }
  };

  const handleUndo = async () => {
    if (!undoState.item) return;
    if (undoState.timerId) {
      clearTimeout(undoState.timerId);
    }
    const eventId = undoState.item.eventId || undoState.item.event_id;
    const result = await addToSchedule({ id: eventId });
    setUndoState({ item: null, timerId: null });
    setFeedback({
      show: true,
      message: result.message || 'Event restored to schedule',
      type: result.success ? 'success' : 'error'
    });
    setTimeout(() => {
      setFeedback({ show: false, message: '', type: '' });
    }, 3000);
  };

  // --- Filter and normalize times ---
  const filteredSchedule = userSchedule
    .filter(item => item.day?.trim().toLowerCase() === selectedDay)
    .map(item => ({ ...item, time: normalizeTime(item.time) }))
    .sort((a, b) => toMinutes(a.time) - toMinutes(b.time));

  // --- Group events by start time to show conflicts side by side ---
  const groupedSchedule = filteredSchedule.reduce((acc, item) => {
    const startTime = item.time.split('-')[0].trim(); // key by start time
    acc[startTime] = acc[startTime] || [];
    acc[startTime].push(item);
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Schedule</h1>
            <p className="text-gray-600">Manage your personal conference schedule</p>
          </div>
          <Link to="/events" className="btn-primary flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            Add Event
          </Link>
        </div>

        {/* Feedback Message */}
        {feedback.show && (
          <div className={`mb-4 p-4 rounded-lg flex items-center justify-between ${
            feedback.type === 'success' 
              ? 'bg-green-100 border border-green-400 text-green-700' 
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            <span>{feedback.message}</span>
            {undoState.item && feedback.type === 'success' && (
              <button
                onClick={handleUndo}
                className="ml-4 px-3 py-1 rounded-md border border-current text-sm font-medium hover:bg-white/30 transition-colors"
              >
                Undo
              </button>
            )}
          </div>
        )}

        {/* Day Navigation */}
        <div className="flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0 mb-6">
          {days.map(day => (
            <button
              key={day.value}
              onClick={() => setSelectedDay(day.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors w-full sm:w-auto ${
                selectedDay === day.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {day.label}
            </button>
          ))}
        </div>
      </div>

      {/* Schedule for Selected Day */}
      <div className="space-y-4">
        {Object.keys(groupedSchedule).length > 0 ? (
  Object.entries(groupedSchedule).map(([startTime, eventsAtSameTime]) => {
    const isConflict = eventsAtSameTime.length > 1; // more than one event at this time
    const columnTemplate = eventsAtSameTime.length > 1
      ? `repeat(${eventsAtSameTime.length}, minmax(0, 1fr))`
      : '1fr';

    return (
      <div
        key={startTime}
        className={`grid gap-4 ${isConflict ? 'bg-yellow-50 p-2 rounded-lg' : ''}`}
        style={{ gridTemplateColumns: columnTemplate }}
      >
        {eventsAtSameTime.map(item => (
          <div
            key={item.id}
            className={`card border-l-4 ${
              isConflict ? 'border-yellow-400' : 'border-primary-500'
            }`}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  {(() => {
                    const categoryKey = getEventCategoryKey(item);
                    return (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(categoryKey)}`}
                      >
                        {getCategoryLabel(categoryKey)}
                      </span>
                    );
                  })()}
                  <button
                    onClick={() => handleRemoveFromSchedule(item.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>

                <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-600">
                  {item.speaker ? (
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{item.speaker}</span>
                    </div>
                  ) : null}
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{item.time}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{item.location}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  })
) : (
  <div className="card text-center py-12">
    <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-2">No events scheduled</h3>
    <p className="text-gray-600 mb-4">
      You haven't added any events to your schedule for {days.find(d => d.value === selectedDay)?.date}.
    </p>
    <Link to="/events" className="btn-primary">
      Browse Events
    </Link>
  </div>
)}

      </div>

    </div>
  );
};

export default Schedule;
